import {
  SIGN_IN_REQUEST,
  SIGN_IN_FAILED,
  SIGN_IN_SUCCESS,
  RESET_AUTH,
  AUTH_LOADING_STATE,
  RESET_PASSWORD,
  AUTH_ERRORS,
  FETCH_ME,
} from '@constants';
import { fetchMe, exchangeToken } from 'thunk';

import { ActionInterface, ErrorInterface } from 'types';
import { createError } from 'utils';

export type User = {
  auth_token: string;
  temp_token: string;
  isAuthTokenFetched?: boolean;
  is_super_admin?: boolean;
  account: {
    id: number;
    email: string;
    slug: string;
    default_team: {
      id: number;
    };
    first_name: string;
    last_name: string;
    initials: string;
    is_integration_account: boolean;
    name: string;
    role_ids: number[];
    teams_roles: Record<number, number[]>;
  };
};

export type AuthStateType = {
  user?: User;
  loadingState: {
    [AUTH_LOADING_STATE.AUTHENTICATION_LOADING]: boolean;
    [AUTH_LOADING_STATE.RESET_PASSWORD_LOADING]: boolean;
    [AUTH_LOADING_STATE.FETCH_ME]: boolean;
  };
  error: {
    [AUTH_ERRORS.AUTHENTICATION_ERROR]?: ErrorInterface;
    [AUTH_ERRORS.RESET_PASSWORD_ERROR]?: boolean;
    [AUTH_ERRORS.FETCH_ME]?: boolean;
  };
};

const INITIAL_STATE = {
  user: undefined,
  loadingState: {
    [AUTH_LOADING_STATE.AUTHENTICATION_LOADING]: false,
    [AUTH_LOADING_STATE.RESET_PASSWORD_LOADING]: false,
    [AUTH_LOADING_STATE.FETCH_ME]: false,
  },
  error: {
    [AUTH_ERRORS.AUTHENTICATION_ERROR]: undefined,
    [AUTH_ERRORS.RESET_PASSWORD_ERROR]: undefined,
    [AUTH_ERRORS.FETCH_ME]: false,
  },
};

export default function reducer(
  state: AuthStateType = INITIAL_STATE,
  actions: ActionInterface<unknown, unknown>,
): AuthStateType {
  const { type, payload, error } = actions;

  /** Create new error state */
  const ERROR = createError(state.error, error as unknown as Record<string, unknown>);
  const LOADING_STATE = {
    ...state.loadingState,
  };

  switch (type) {
    /** Request */
    case SIGN_IN_REQUEST: {
      LOADING_STATE[AUTH_LOADING_STATE.AUTHENTICATION_LOADING] = true;

      return {
        ...state,
        loadingState: LOADING_STATE,
      };
    }

    case FETCH_ME.REQUEST: {
      LOADING_STATE[AUTH_LOADING_STATE.FETCH_ME] = true;

      return {
        ...state,
        loadingState: LOADING_STATE,
      };
    }

    case RESET_PASSWORD.REQUEST:
      LOADING_STATE[AUTH_LOADING_STATE.RESET_PASSWORD_LOADING] = true;

      return {
        ...state,
        loadingState: LOADING_STATE,
      };

    /** Success */

    /** Deprecated */
    case SIGN_IN_SUCCESS: {
      LOADING_STATE[AUTH_LOADING_STATE.AUTHENTICATION_LOADING] = false;
      ERROR[AUTH_ERRORS.AUTHENTICATION_ERROR] = undefined;

      // Type assertion: payload should be ActionSuccessInterface with responsePayload
      const successPayload = payload as unknown as { responsePayload: { data: User } };

      return {
        ...state,
        user: {
          ...successPayload.responsePayload.data,
          is_super_admin: true,
        },
        loadingState: LOADING_STATE,
        error: ERROR,
      };
    }

    case RESET_PASSWORD.SUCCESS:
      LOADING_STATE[AUTH_LOADING_STATE.RESET_PASSWORD_LOADING] = false;
      ERROR[AUTH_ERRORS.RESET_PASSWORD_ERROR] = undefined;
      return {
        ...state,
        loadingState: LOADING_STATE,
        error: ERROR,
      };

    case exchangeToken.fulfilled.toString(): {
      LOADING_STATE[AUTH_LOADING_STATE.AUTHENTICATION_LOADING] = false;
      ERROR[AUTH_ERRORS.AUTHENTICATION_ERROR] = undefined;

      // Type assertion: exchangeToken returns ExchangeTokenSuccessResponse which can be cast to User
      const userPayload = actions.payload as unknown as User;

      return {
        ...state,
        user: userPayload, // Exchange token contains user information already
        loadingState: LOADING_STATE,
        error: ERROR,
      };
    }

    case fetchMe.fulfilled.toString(): {
      LOADING_STATE[AUTH_LOADING_STATE.FETCH_ME] = false;
      ERROR[AUTH_ERRORS.FETCH_ME] = undefined;

      // Type assertion: fetchMe returns User
      const mePayload = actions.payload as unknown as User;

      return {
        ...state,
        user: {
          ...state.user,
          ...mePayload,
        },
        loadingState: LOADING_STATE,
      };
    }

    /** Failed */

    /** Deprecated */
    case SIGN_IN_FAILED:
      LOADING_STATE[AUTH_LOADING_STATE.AUTHENTICATION_LOADING] = false;

      return {
        ...state,
        error: ERROR,
        loadingState: LOADING_STATE,
      };

    case RESET_PASSWORD.FAILURE:
      LOADING_STATE[AUTH_LOADING_STATE.RESET_PASSWORD_LOADING] = false;
      return {
        ...state,
        error: ERROR,
        loadingState: LOADING_STATE,
      };

    case RESET_AUTH:
      return {
        ...INITIAL_STATE,
      };

    case exchangeToken.rejected.toString(): {
      LOADING_STATE[AUTH_LOADING_STATE.AUTHENTICATION_LOADING] = false;

      return {
        ...state,
        loadingState: LOADING_STATE,
        error: ERROR,
      };
    }

    case FETCH_ME.FAILED: {
      LOADING_STATE[AUTH_LOADING_STATE.FETCH_ME] = false;

      return {
        ...state,
        loadingState: LOADING_STATE,
        error: ERROR,
      };
    }

    default:
      return state;
  }
}
